import { lazy, Suspense, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/Header';
import Hero from '@/components/Hero';
import SubventionStrip from '@/components/SubventionStrip';
import TrustStrip from '@/components/TrustStrip';
import ProjectSnapshot from '@/components/ProjectSnapshot';
import SEOHead from '@/components/SEOHead';
import Footer from '@/components/Footer';
import DynamicImportErrorBoundary from '@/components/DynamicImportErrorBoundary';
import { useIdleCallback } from '@/hooks/useIdleCallback';
import { projectData } from '@/data/project';

// ApartmentComplex JSON-LD schema for homepage
const apartmentComplexSchema = {
  '@context': 'https://schema.org',
  '@type': 'ApartmentComplex',
  name: projectData.projectName,
  description: 'Premium 3 & 4 BHK residences in Chandivali–Powai, Mumbai with low-density planning and sustainability-led design.',
  url: projectData.domain,
  telephone: projectData.contact.phone,
  email: projectData.contact.email,
  address: {
    '@type': 'PostalAddress',
    streetAddress: projectData.location.address,
    addressLocality: 'Chandivali',
    addressRegion: projectData.location.state,
    postalCode: projectData.location.pincode,
    addressCountry: 'IN',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: projectData.location.coordinates.lat,
    longitude: projectData.location.coordinates.lng,
  },
  amenityFeature: [
    {
      '@type': 'LocationFeatureSpecification',
      name: 'Freehold Land',
      value: true,
      description: 'Built on freehold land offering permanent ownership without lease dependency.',
    },
    {
      '@type': 'LocationFeatureSpecification',
      name: 'Low-Density Design',
      value: true,
      description: 'Only four residences per floor for greater privacy and reduced congestion.',
    },
    {
      '@type': 'LocationFeatureSpecification',
      name: 'Covered Surface Parking',
      value: true,
      description: 'Covered surface parking designed for easier access and everyday convenience.',
    },
    {
      '@type': 'LocationFeatureSpecification',
      name: '10+ Ft Clear Height',
      value: true,
      description: 'Higher clear ceiling height supporting better ventilation and spatial comfort.',
    },
    {
      '@type': 'LocationFeatureSpecification',
      name: 'Miyawaki Urban Forest',
      value: true,
      description: 'Sustainability philosophy inspired by Miyawaki methodology and Nahar Amrit Shakti Udyan legacy.',
    },
  ],
  numberOfBedrooms: {
    '@type': 'QuantitativeValue',
    minValue: 2,
    maxValue: 4,
  },
  floorSize: {
    '@type': 'QuantitativeValue',
    minValue: 886,
    maxValue: 2487,
    unitCode: 'FTK',
  },
  petsAllowed: false,
};

// Lazy load below-the-fold components
const ProjectDifferentiators = lazy(() => import('@/components/ProjectDifferentiators'));
const PricingSection = lazy(() => import('@/components/PricingSection'));
const FloorPlanPreview = lazy(() => import('@/components/FloorPlanPreview'));
const AmenitiesSection = lazy(() => import('@/components/AmenitiesSection'));
const LocationSection = lazy(() => import('@/components/LocationSection'));
const ComparisonTabs = lazy(() => import('@/components/ComparisonTabs'));
const VirtualTourPreview = lazy(() => import('@/components/VirtualTourPreview'));
const FAQSection = lazy(() => import('@/components/FAQSection'));
const ContactSection = lazy(() => import('@/components/ContactSection'));
const GoldCampaignSection = lazy(() => import('@/components/GoldCampaignSection'));

// Deferred lower-priority sections
const GallerySection = lazy(() => import('@/components/GallerySection'));
const BrochurePreview = lazy(() => import('@/components/BrochurePreview'));
const EcosystemSection = lazy(() => import('@/components/EcosystemSection'));
const LocalLandmarksBlock = lazy(() => import('@/components/LocalLandmarksBlock'));
const LatestUpdates = lazy(() => import('@/components/LatestUpdates'));
const HomepageSEOAuthorityBlock = lazy(() => import('@/components/HomepageSEOAuthorityBlock'));

// Floating widgets
const WhatsAppWidget = lazy(() => import('@/components/WhatsAppWidget'));
const FloatingCTA = lazy(() => import('@/components/FloatingCTA'));
const ExitIntentPopup = lazy(() => import('@/components/ExitIntentPopup'));
const DismissibleCampaignRibbon = lazy(() => import('@/components/DismissibleCampaignRibbon'));
const SectionNavigator = lazy(() => import('@/components/SectionNavigator'));

// Optimized skeleton - minimal, no animation to prevent jank
const SectionSkeleton = ({ height = '400px' }: { height?: string }) => (
  <div 
    className="bg-muted/30" 
    style={{ minHeight: height }}
    aria-hidden="true" 
  />
);

const Index = () => {
  // Defer floating widgets until browser is idle - ribbon loads faster (500ms)
  const widgetsReady = useIdleCallback(500, true);
  const [ribbonVisible, setRibbonVisible] = useState(false);
  
  const handleRibbonVisibilityChange = useCallback((visible: boolean) => {
    setRibbonVisible(visible);
  }, []);

  return (
    <DynamicImportErrorBoundary>
      <SEOHead type="home" path="/" />
      {/* ApartmentComplex JSON-LD for rich results */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(apartmentComplexSchema)}
        </script>
      </Helmet>
      {/* 30:70 Subvention Plan announcement strip — sticky at top, above header */}
      <SubventionStrip />
      {/* Push the fixed header below the strip (44px desktop, 56px mobile) */}
      <style>{`
        #site-header { top: 56px !important; }
        @media (min-width: 768px) { #site-header { top: 44px !important; } }
      `}</style>
      <Header ribbonVisible={ribbonVisible} />
      <main
        id="main-content"
        className="pt-[56px] md:pt-[44px]"
      >
        {/* ScrollSpy anchor for "Overview" (top of content) */}
        <div id="overview-anchor" />

        {/* ======== CONVERSION FUNNEL: ABOVE THE FOLD ======== */}

        {/* A. Hero - What is this + primary benefit + CTAs */}
        <Hero />
        
        {/* B. Trust Strip - RERA, Developer, Possession, Configs */}
        <TrustStrip />
        
        {/* Project Snapshot Pills */}
        <ProjectSnapshot />
        
        {/* ======== SEO AUTHORITY BLOCK - Crawl-visible content ======== */}
        <Suspense fallback={<SectionSkeleton height="500px" />}>
          <HomepageSEOAuthorityBlock />
        </Suspense>
        
        {/* ======== C. WHY THIS PROJECT (3-5 value cards) ======== */}
        <Suspense fallback={<SectionSkeleton height="350px" />}>
          <ProjectDifferentiators />
        </Suspense>
        
        {/* ======== D. CONFIGURATIONS & PRICING TEASER ======== */}
        <div id="pricing-anchor" />
        <Suspense fallback={<SectionSkeleton height="500px" />}>
          <PricingSection />
        </Suspense>
        
        {/* ======== E. LAYOUTS PREVIEW ======== */}
        <div id="floor-plans-anchor" />
        <Suspense fallback={<SectionSkeleton height="450px" />}>
          <FloorPlanPreview />
        </Suspense>
        
        {/* ======== F. AMENITIES HIGHLIGHTS ======== */}
        <div id="amenities-anchor" />
        <Suspense fallback={<SectionSkeleton height="450px" />}>
          <AmenitiesSection />
        </Suspense>
        
        {/* ======== G. LOCATION TEASER ======== */}
        <div id="location-anchor" />
        <Suspense fallback={<SectionSkeleton height="450px" />}>
          <LocationSection />
        </Suspense>
        
        {/* ======== H. COMPARISON TEASER ======== */}
        <Suspense fallback={<SectionSkeleton height="350px" />}>
          <ComparisonTabs />
        </Suspense>
        
        {/* ======== I. VIRTUAL TOUR (mid-funnel) ======== */}
        <div id="virtual-tour-anchor" />
        <Suspense fallback={<SectionSkeleton height="400px" />}>
          <VirtualTourPreview />
        </Suspense>
        
        {/* ======== J. TOP FAQs (accordion) ======== */}
        <div id="faq-anchor" />
        <Suspense fallback={<SectionSkeleton height="400px" />}>
          <FAQSection limit={10} showViewAllLink />
        </Suspense>
        
        {/* ======== K. FINAL CONVERSION BLOCK ======== */}
        <Suspense fallback={<SectionSkeleton height="400px" />}>
          <ContactSection />
        </Suspense>
        
        {/* ======== SUPPORTING CONTENT (below main funnel) ======== */}
        
        {/* Gold Campaign - Promotional */}
        <Suspense fallback={<SectionSkeleton height="350px" />}>
          <GoldCampaignSection />
        </Suspense>
        
        {/* Gallery Preview */}
        <div id="gallery-anchor" />
        <Suspense fallback={<SectionSkeleton height="400px" />}>
          <GallerySection />
        </Suspense>
        
        {/* Brochure Download */}
        <Suspense fallback={<SectionSkeleton height="300px" />}>
          <BrochurePreview />
        </Suspense>
        
        {/* Neighbourhood Ecosystem */}
        <Suspense fallback={<SectionSkeleton height="350px" />}>
          <LocalLandmarksBlock />
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton height="350px" />}>
          <EcosystemSection />
        </Suspense>
        
        {/* Latest Updates */}
        <Suspense fallback={<SectionSkeleton height="300px" />}>
          <LatestUpdates />
        </Suspense>
      </main>
      <Footer />
      
      {/* Floating widgets - deferred until idle */}
      {widgetsReady && (
        <>
          <Suspense fallback={null}>
            <DismissibleCampaignRibbon onVisibilityChange={handleRibbonVisibilityChange} />
          </Suspense>
          <Suspense fallback={null}>
            <SectionNavigator />
          </Suspense>
          <Suspense fallback={null}>
            <WhatsAppWidget />
          </Suspense>
          <Suspense fallback={null}>
            <FloatingCTA />
          </Suspense>
          <Suspense fallback={null}>
            <ExitIntentPopup />
          </Suspense>
        </>
      )}
    </DynamicImportErrorBoundary>
  );
};

export default Index;